// src/tattoo/commentsState.js
import { proxy } from "valtio";
import { getViewerId } from "./share"; // add at top if not already

/**
 * Central comments store
 *
 * - threads:        layerId -> [comment rows]
 * - attachments:    commentId -> [attachment rows]
 * - reactionCounts: commentId -> { [emoji]: count }
 * - myReactions:    commentId -> { [emoji]: true }
 */
export const commentsStore = proxy({
  // layerId -> array of comment rows from Supabase view
  threads: {},

  // commentId -> attachment[]
  attachments: {},

  // commentId -> { [emoji]: number }
  reactionCounts: {},

  // commentId -> { [emoji]: true } for current user
  myReactions: {},

  // --- core mutators used by commentsApi.js ---

  setThread(layerId, rows) {
    if (!layerId) return;
    const nextThreads = { ...this.threads };
    nextThreads[layerId] = Array.isArray(rows) ? [...rows] : [];
    this.threads = nextThreads;
  },

  add(layerId, row) {
    if (!layerId || !row) return;
    const existing = this.threads[layerId] || [];
    const idx = existing.findIndex((c) => c.id === row.id);
    let nextArr;
    if (idx >= 0) {
      nextArr = [...existing];
      nextArr[idx] = row;
    } else {
      nextArr = [...existing, row];
    }
    this.threads = {
      ...this.threads,
      [layerId]: nextArr,
    };
  },

  updateOne(layerId, row) {
    if (!layerId || !row) return;
    const existing = this.threads[layerId] || [];
    const idx = existing.findIndex((c) => c.id === row.id);
    if (idx < 0) return;
    const nextArr = [...existing];
    nextArr[idx] = row;
    this.threads = {
      ...this.threads,
      [layerId]: nextArr,
    };
  },

  remove(layerId, id) {
    if (!layerId || !id) return;
    const existing = this.threads[layerId] || [];
    const nextArr = existing.filter((c) => c.id !== id);
    this.threads = {
      ...this.threads,
      [layerId]: nextArr,
    };
  },
});

/* ---------- selectors used by UI ---------- */

export function getLayerComments(layerId) {
  return commentsStore.threads[layerId] || [];
}

export function unresolvedCount(layerId) {
  const arr = commentsStore.threads[layerId] || [];
  return arr.filter((c) => !c.resolved).length;
}


export function unackedCount(layerId) {
  const arr = commentsStore.threads[layerId] || [];
  const vid = getViewerId();
  return arr.filter((c) => !c?.ack_by?.[vid]).length;
}

/* ---------- wrapper exports (unchanged API) ---------- */

export function setThread(layerId, rows) {
  commentsStore.setThread(layerId, rows);
}

export function addRow(layerId, row) {
  commentsStore.add(layerId, row);
}

export function updateRow(layerId, row) {
  commentsStore.updateOne(layerId, row);
}

export function removeRow(layerId, id) {
  commentsStore.remove(layerId, id);
}

/* ---------- NEW: attachment & reaction selectors ---------- */

export function getCommentAttachments(commentId) {
  return commentsStore.attachments[commentId] || [];
}

export function getCommentReactionCounts(commentId) {
  return commentsStore.reactionCounts[commentId] || {};
}

export function getMyCommentReactions(commentId) {
  return commentsStore.myReactions[commentId] || {};
}

/* ---------- NEW: realtime patch helpers ---------- */

/**
 * Called by realtime listener for INSERT/UPDATE/DELETE on attachments table.
 */
export function applyAttachmentChange(payload) {
  const row = payload.new || payload.old;
  if (!row) return;

  const commentId = row.comment_id;
  if (!commentId) return;

  const list = commentsStore.attachments[commentId] || [];

  if (payload.eventType === "INSERT") {
    const exists = list.some((a) => a.id === row.id);
    if (!exists) {
      commentsStore.attachments[commentId] = [...list, row];
    }
  } else if (payload.eventType === "UPDATE") {
    commentsStore.attachments[commentId] = list.map((a) =>
      a.id === row.id ? { ...a, ...row } : a
    );
  } else if (payload.eventType === "DELETE") {
    commentsStore.attachments[commentId] = list.filter((a) => a.id !== row.id);
  }
}

/**
 * Called by realtime listener for INSERT/DELETE/UPDATE on reactions table.
 * `currentUserId` is used so the store knows which reactions belong to "me".
 */
export function applyReactionChange(payload, currentUserId) {
  const row = payload.new || payload.old;
  if (!row) return;

  const commentId = row.comment_id;
  if (!commentId) return;

  const emoji = row.emoji || row.type || "like";

  const counts =
    commentsStore.reactionCounts[commentId] ||
    (commentsStore.reactionCounts[commentId] = {});
  const mine =
    commentsStore.myReactions[commentId] ||
    (commentsStore.myReactions[commentId] = {});

  const isMe = currentUserId && row.user_id === currentUserId;

  const bump = (delta) => {
    const next = (counts[emoji] || 0) + delta;
    counts[emoji] = Math.max(0, next);
  };

  if (payload.eventType === "INSERT") {
    bump(1);
    if (isMe) mine[emoji] = true;
  } else if (payload.eventType === "DELETE") {
    bump(-1);
    if (isMe) delete mine[emoji];
  } else if (payload.eventType === "UPDATE") {
    const oldEmoji = payload.old?.emoji || payload.old?.type || emoji;
    const newEmoji = payload.new?.emoji || payload.new?.type || emoji;
    if (oldEmoji !== newEmoji) {
      const oldCount = counts[oldEmoji] || 0;
      if (oldCount > 0) counts[oldEmoji] = oldCount - 1;
      counts[newEmoji] = (counts[newEmoji] || 0) + 1;

      if (isMe) {
        delete mine[oldEmoji];
        mine[newEmoji] = true;
      }
    }
  }
}